/**
 * LobbyToolbar - Host controls for lobby settings
 *
 * Displays privacy toggle, spectator controls, NPC controls, and settings button.
 */

interface LobbyToolbarProps {
  isPublic: boolean;
  allowSpectators: boolean;
  spectatorCount: number;
  showNpcs: boolean;
  isHost: boolean;
  onTogglePublic: () => void;
  onToggleSpectators: () => void;
  onToggleNpcs: () => void;
  onOpenSettings: () => void;
}

export function LobbyToolbar({
  isPublic,
  allowSpectators,
  spectatorCount,
  showNpcs,
  isHost,
  onTogglePublic,
  onToggleSpectators,
  onToggleNpcs,
  onOpenSettings,
}: LobbyToolbarProps) {
  if (!isHost) {
    // Non-hosts see a read-only view
    return (
      <div
        className="flex items-center gap-3 mb-4 bg-white/5 rounded-lg p-3"
        data-testid="lobby-toolbar-readonly"
      >
        <span className="text-sm text-gray-400">
          🔒 {isPublic ? "Public" : "Private"} Room
        </span>
        {allowSpectators && spectatorCount > 0 && (
          <span className="text-sm text-gray-400">
            👁️ {spectatorCount} watching
          </span>
        )}
      </div>
    );
  }

  // Host view with controls
  return (
    <div
      className="flex flex-wrap items-center gap-3 mb-4 bg-white/5 rounded-lg p-3"
      data-testid="lobby-toolbar"
    >
      {/* Privacy Toggle */}
      <button
        onClick={onTogglePublic}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
          isPublic
            ? "bg-green-600 hover:bg-green-700 text-white"
            : "bg-gray-600 hover:bg-gray-700 text-white"
        }`}
        type="button"
        data-testid="lobby-privacy-button"
        title={
          isPublic
            ? "Room is public (visible to all)"
            : "Room is private (invite only)"
        }
      >
        <span>{isPublic ? "🔓" : "🔒"}</span>
        <span>{isPublic ? "Public" : "Private"}</span>
      </button>

      {/* Spectators Toggle */}
      <button
        onClick={onToggleSpectators}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
          allowSpectators
            ? "bg-blue-600 hover:bg-blue-700 text-white"
            : "bg-gray-600 hover:bg-gray-700 text-white"
        }`}
        type="button"
        data-testid="lobby-spectators-button"
        title={
          allowSpectators ? "Spectators allowed" : "Spectators not allowed"
        }
      >
        <span>👁️</span>
        <span>Spectators: {allowSpectators ? "✓" : "✗"}</span>
      </button>

      {/* Spectator Count (if enabled and > 0) */}
      {allowSpectators && spectatorCount > 0 && (
        <div
          className="px-3 py-2 bg-white/10 rounded-lg text-sm"
          data-testid="lobby-spectator-count"
        >
          <span className="text-gray-300">👁️ {spectatorCount} watching</span>
        </div>
      )}

      {/* NPCs Toggle */}
      <button
        onClick={onToggleNpcs}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
          showNpcs
            ? "bg-indigo-600 hover:bg-indigo-700 text-white"
            : "bg-gray-600 hover:bg-gray-700 text-white"
        }`}
        type="button"
        data-testid="lobby-npcs-button"
        title={showNpcs ? "Hide AI players" : "Show AI players"}
      >
        <span>🤖</span>
        <span>NPCs: {showNpcs ? "✓" : "✗"}</span>
      </button>

      {/* Settings Button */}
      <button
        onClick={onOpenSettings}
        className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition ml-auto"
        type="button"
        data-testid="lobby-settings-button"
        title="Open game settings"
      >
        <span>⚙️</span>
        <span>Settings</span>
      </button>
    </div>
  );
}
